/* RevisaoLegada — Tela dedicada para revisão de sementes geradas pelo
 * enriquecimento da Base Legada LSC (origem_cadastro="legada_enriquecida").
 *
 * Permite ao revisor/coordenador comparar diretamente os dados originais da
 * planilha histórica com a sugestão de classificação prospectiva feita pelo
 * Gemini, antes de aprovar formalmente.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { AREAS, CLASSIFICACOES, STATUS, labelOf } from "@/lib/constants";
import { RefreshCcw, Search, Database, Brain, FileWarning, FileCheck2, FileX2, Archive, Eye, Filter } from "lucide-react";
import LegadoDiffModal from "@/components/LegadoDiffModal";
import ModuleHelp from "@/components/help/ModuleHelp";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

const statusBadge = (s) => {
  const def = STATUS.find((x) => x.value === s);
  return <span className={`badge ${def?.className || "badge-muted"}`}>{def?.label || s}</span>;
};

const STATUS_OPTIONS = [
  { value: "pendente", label: "Pendentes", icon: FileWarning, color: "text-[var(--warn)]" },
  { value: "aprovado", label: "Aprovadas", icon: FileCheck2, color: "text-[var(--success)]" },
  { value: "rejeitado", label: "Rejeitadas", icon: FileX2, color: "text-[var(--danger)]" },
  { value: "arquivado", label: "Arquivadas", icon: Archive, color: "text-[var(--text-muted)]" },
  { value: "", label: "Todas", icon: Filter, color: "text-[var(--text-muted)]" },
];

export default function RevisaoLegada() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [openSeed, setOpenSeed] = useState(null);
  const [filters, setFilters] = useState({ q: "", area: "", status: "pendente" });
  const [counts, setCounts] = useState({ pendente: 0, aprovado: 0, rejeitado: 0, arquivado: 0 });

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = { origem_cadastro: "legada_enriquecida", limit: 500 };
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;
      if (filters.area) params.area = filters.area;
      const { data } = await api.get("/seeds", { params });
      setItems(data || []);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Contadores por status (carregados em paralelo)
  const loadCounts = useCallback(async () => {
    try {
      const promises = ["pendente", "aprovado", "rejeitado", "arquivado"].map((st) =>
        api.get("/seeds", { params: { origem_cadastro: "legada_enriquecida", status: st, limit: 1000 } })
          .then((r) => [st, (r.data || []).length])
      );
      const results = await Promise.all(promises);
      setCounts(Object.fromEntries(results));
    } catch (e) {
      console.warn("KPIs auxiliares falharam:", e);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const totalNaTela = items.length;
  const mediaScore = useMemo(() => {
    if (items.length === 0) return null;
    const arr = items.map((i) => i.confianca_ia ?? i.ai_confidence_score ?? 0).filter((s) => s > 0);
    if (arr.length === 0) return null;
    return (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(1);
  }, [items]);

  return (
    <div className="space-y-4" data-testid="revisao-legada-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-mil text-[var(--gold)] flex items-center gap-1.5">
            <Database size={11} /> Revisão Humana · Base Legada Enriquecida
          </div>
          <h1 className="font-display font-bold text-3xl mt-1 flex items-center gap-3">Diff Humano <span className="text-[var(--gold)]">×</span> IA <ModuleHelp module="revisaoLegada" /></h1>
          <p className="text-xs text-[var(--text-muted)] mt-1 max-w-3xl">
            Análise comparativa entre o registro humano da planilha histórica do LSC/EGN e a classificação prospectiva sugerida pelo Gemini. Nenhuma semente legada é aprovada automaticamente — o revisor decide caso a caso.
          </p>
        </div>
        <button onClick={load} className="btn-ghost text-xs flex items-center gap-1.5" data-testid="btn-refresh">
          <RefreshCcw size={12} /> Recarregar
        </button>
      </div>

      {/* KPIs por status */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="legada-kpis">
        {STATUS_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const isActive = filters.status === opt.value;
          const count = opt.value ? counts[opt.value] : Object.values(counts).reduce((a, b) => a + b, 0);
          return (
            <button
              key={opt.value || "all"}
              onClick={() => setFilters((f) => ({ ...f, status: opt.value }))}
              className={`card-mil p-4 text-left transition hover:border-[var(--gold)] hover:-translate-y-0.5
                          ${isActive ? "border-[var(--gold)] gold-accent-top" : ""}`}
              data-testid={`filter-status-${opt.value || "all"}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-mil text-[10px]">{opt.label}</div>
                  <div className="kpi-num mt-1">{count ?? "—"}</div>
                </div>
                <Icon size={18} className={opt.color} strokeWidth={1.5} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="card-mil p-3 flex flex-wrap items-center gap-2" data-testid="legada-toolbar">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            className="input-mil pl-9 text-xs"
            placeholder="Buscar por título, tema, palavra-chave…"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            data-testid="legada-search"
          />
        </div>
        <select
          className="input-mil text-xs max-w-[260px]"
          value={filters.area}
          onChange={(e) => setFilters((f) => ({ ...f, area: e.target.value }))}
          data-testid="legada-area-filter"
        >
          <option value="">Todas as áreas</option>
          {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <div className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-widest ml-auto">
          {loading ? "Carregando…" : `${totalNaTela} registros · score IA médio: ${mediaScore ?? "—"}`}
        </div>
      </div>

      {/* Erro */}
      {err && (
        <div className="card-mil p-3 text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10">
          {err}
        </div>
      )}

      {/* Tabela */}
      <div className="card-mil overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="legada-table">
            <thead className="bg-[var(--surface-2)]">
              <tr>
                <th className="px-4 py-3 label-mil text-left">ID Legado</th>
                <th className="px-4 py-3 label-mil text-left">Título Original</th>
                <th className="px-4 py-3 label-mil text-left">Tema Legado</th>
                <th className="px-4 py-3 label-mil text-left">Classificação IA</th>
                <th className="px-4 py-3 label-mil text-left">Score IA</th>
                <th className="px-4 py-3 label-mil text-left">Status</th>
                <th className="px-4 py-3 label-mil text-left">Cadastro</th>
                <th className="px-4 py-3 label-mil text-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-[var(--text-muted)] text-sm">
                    Nenhuma semente da Base Legada Enriquecida encontrada para os filtros atuais.
                  </td>
                </tr>
              )}
              {items.map((s) => (
                <tr key={s.seed_id}
                    className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)]/70 cursor-pointer transition-colors"
                    onClick={() => setOpenSeed(s.seed_id)}
                    data-testid={`legada-row-${s.seed_id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--text-muted)]">
                    {s.legacy_id != null ? `#${s.legacy_id}` : "—"}
                  </td>
                  <td className="px-4 py-3 max-w-[320px]">
                    <div className="font-semibold truncate text-[var(--text)]" title={s.source_titulo || s.titulo_insumo}>
                      {s.source_titulo || s.titulo_insumo}
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)] font-mono truncate mt-0.5">
                      {s.source_origem || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs max-w-[180px] truncate">{s.source_origem || "—"}</td>
                  <td className="px-4 py-3">
                    {s.classificacao_semente
                      ? <span className="badge badge-gold text-[10px]">{labelOf(CLASSIFICACOES, s.classificacao_semente)}</span>
                      : <span className="text-[var(--text-muted)] italic text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Brain size={11} className="text-[var(--gold)]" />
                      <span className="font-mono text-xs font-bold">{s.confianca_ia ?? s.ai_confidence_score ?? "—"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{statusBadge(s.status_validacao)}</td>
                  <td className="px-4 py-3 text-[10px] font-mono text-[var(--text-muted)]">
                    {fmtDate(s.data_cadastro || s.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); setOpenSeed(s.seed_id); }}
                      className="btn-primary text-xs inline-flex items-center gap-1"
                      data-testid={`legada-diff-${s.seed_id}`}
                    >
                      <Eye size={11} /> Ver Diff
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LegadoDiffModal
        seedId={openSeed}
        onClose={() => setOpenSeed(null)}
        onChanged={() => { load(); loadCounts(); }}
      />
    </div>
  );
}
